import React, { useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import {
  Bold as BoldIcon, Italic as ItalicIcon, Quote,
  List as BulletIcon, ListOrdered, Link as LinkIcon,
  Image as ImageInsertIcon, Smile, Undo2, Redo2,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { sanitizeForumHtml } from "@/lib/richtext";

// Simple, curated emoji set for the forum picker. Kept short & Unicode-only so
// they insert as plain text (no image dependencies, no server needs).
const EMOJIS = ["😀","😅","😂","🥲","😍","🤔","😊","🙌","👍","👎","🎉","✨","🔥","💯","❤️","😢","😡","🙏","👏","🤝","💡","📚","🏫","🎓","🌟","👀","😴","🤯","😇","🥳"];

const MAX_IMG_BYTES = 5 * 1024 * 1024;

/**
 * ForumRichEditor — TipTap wrapper tuned for forum composer UX.
 *
 * Deliberately narrow toolbar: Bold, Italic, Bullet, Numbered, Quote, Link,
 * Image (inline upload), Emoji, Undo, Redo. No headings, no font/color, no
 * alignment, no tables — this is a forum post, not an article.
 *
 * Emits sanitized HTML via `onChange(html)`. Callers should send the same HTML
 * to backend and render with `<HtmlContent value={...}/>` for XSS safety.
 */
export function ForumRichEditor({ value, onChange, placeholder = "Tulis pesanmu…", testidPrefix = "forum-editor", minHeight = 120, resetKey = 0 }) {
  const fileInputRef = useRef(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        // Use our own Link config below
        link: false,
      }),
      Placeholder.configure({ placeholder }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          class: "text-[#E07A5F] underline underline-offset-2",
          rel: "noopener noreferrer nofollow",
          target: "_blank",
        },
        validate: (href) => /^https?:\/\//i.test(href),
      }),
      Image.configure({
        inline: true,
        allowBase64: false,
        HTMLAttributes: {
          class: "rounded-xl border border-[#EAE7E0] max-w-full h-auto my-3",
        },
      }),
    ],
    content: value || "",
    onUpdate: ({ editor: ed }) => onChange?.(ed.getHTML()),
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none px-3 py-2 text-[#1F2937]",
        "data-testid": `${testidPrefix}-input`,
        style: `min-height: ${minHeight}px;`,
      },
    },
  });

  // Parent can force-clear the editor by bumping `resetKey`. TipTap's internal
  // state is otherwise decoupled from the parent's `value` prop after mount,
  // so `setBody("")` alone doesn't visually clear the editor.
  React.useEffect(() => {
    if (editor && resetKey > 0) {
      editor.commands.setContent("", false);
    }
  }, [resetKey, editor]);

  if (!editor) return null;

  const addLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Tempel URL (https://…)", prev);
    if (url === null) return;
    if (url === "") { editor.chain().focus().unsetLink().run(); return; }
    if (!/^https?:\/\//i.test(url)) { toast.error("URL harus dimulai dengan http:// atau https://"); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const pickImage = () => fileInputRef.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(f.type)) { toast.error("Hanya JPG, PNG, atau WebP."); return; }
    if (f.size > MAX_IMG_BYTES) { toast.error("Maks 5 MB per gambar."); return; }
    const fd = new FormData();
    fd.append("file", f);
    try {
      toast.loading("Mengunggah gambar…", { id: "img-up" });
      const r = await api.post("/forum/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = r.data?.url;
      if (!url) throw new Error("no url");
      editor.chain().focus().setImage({ src: url, alt: f.name }).run();
      toast.success("Gambar disisipkan.", { id: "img-up" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengunggah gambar.", { id: "img-up" });
    }
  };

  const insertEmoji = (em) => {
    editor.chain().focus().insertContent(em).run();
    setEmojiOpen(false);
  };

  const btn = (active) => "inline-flex items-center justify-center w-8 h-8 rounded-md " +
    (active ? "bg-[#1E3A8A] text-white" : "text-[#4B5563] hover:bg-[#F3F1ED]");

  return (
    <div className="rounded-xl border border-[#EAE7E0] bg-white relative" data-testid={testidPrefix}>
      <div className="flex items-center gap-1 flex-wrap px-2 py-1.5 border-b border-[#EAE7E0]">
        <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={btn(editor.isActive("bold"))} data-testid={`${testidPrefix}-bold`} title="Bold"><BoldIcon size={15} /></button>
        <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={btn(editor.isActive("italic"))} data-testid={`${testidPrefix}-italic`} title="Italic"><ItalicIcon size={15} /></button>
        <span className="w-px h-5 bg-[#EAE7E0] mx-1" />
        <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={btn(editor.isActive("bulletList"))} data-testid={`${testidPrefix}-bullet`} title="Bullet list"><BulletIcon size={15} /></button>
        <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={btn(editor.isActive("orderedList"))} data-testid={`${testidPrefix}-numbered`} title="Numbered list"><ListOrdered size={15} /></button>
        <button type="button" onClick={() => editor.chain().focus().toggleBlockquote().run()} className={btn(editor.isActive("blockquote"))} data-testid={`${testidPrefix}-quote`} title="Quote"><Quote size={15} /></button>
        <span className="w-px h-5 bg-[#EAE7E0] mx-1" />
        <button type="button" onClick={addLink} className={btn(editor.isActive("link"))} data-testid={`${testidPrefix}-link`} title="Insert link"><LinkIcon size={15} /></button>
        <button type="button" onClick={pickImage} className={btn(false)} data-testid={`${testidPrefix}-image`} title="Insert image"><ImageInsertIcon size={15} /></button>
        <button type="button" onClick={() => setEmojiOpen((v) => !v)} className={btn(emojiOpen)} data-testid={`${testidPrefix}-emoji`} title="Insert emoji"><Smile size={15} /></button>
        <span className="w-px h-5 bg-[#EAE7E0] mx-1" />
        <button type="button" onClick={() => editor.chain().focus().undo().run()} className={btn(false)} data-testid={`${testidPrefix}-undo`} title="Undo"><Undo2 size={15} /></button>
        <button type="button" onClick={() => editor.chain().focus().redo().run()} className={btn(false)} data-testid={`${testidPrefix}-redo`} title="Redo"><Redo2 size={15} /></button>
        <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} data-testid={`${testidPrefix}-file-input`} />
      </div>
      {emojiOpen && (
        <div className="absolute z-20 mt-1 left-2 top-11 bg-white border border-[#EAE7E0] rounded-lg shadow p-2 grid grid-cols-6 gap-1 max-w-[260px]" data-testid={`${testidPrefix}-emoji-picker`}>
          {EMOJIS.map((e) => (
            <button key={e} type="button" onClick={() => insertEmoji(e)} className="text-lg hover:bg-[#F3F1ED] rounded" data-testid={`${testidPrefix}-emoji-${e}`}>{e}</button>
          ))}
        </div>
      )}
      <EditorContent editor={editor} />
    </div>
  );
}

/**
 * HtmlContent — safe renderer for forum body text.
 *
 * Detects if `value` looks like HTML (has any tag). If plain text (legacy
 * pre-Feb-2026 threads/replies), renders as `whitespace-pre-line` so it stays
 * pixel-identical to the old renderer — no HTML escaping artifacts. If HTML,
 * runs through `sanitizeForumHtml` and dangerouslySetInnerHTML.
 */
export function HtmlContent({ value, className = "", testid }) {
  const s = (value || "").trim();
  const isHtml = /<[a-z][^>]*>/i.test(s);
  if (!isHtml) {
    return <div className={`whitespace-pre-line ${className}`} data-testid={testid}>{s}</div>;
  }
  const clean = sanitizeForumHtml(s);
  return (
    <div
      className={`forum-rich-content ${className}`}
      data-testid={testid}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}
